/* ==========================================================================
   FITRAH — storefront behaviour
   Independent modules, no dependencies. Every cart operation goes through
   Shopify's own AJAX Cart API; nothing is tracked client-side.
   ========================================================================== */

(function () {
  'use strict';

  var announce = (function () {
    var region = document.getElementById('LiveRegion');
    return function (message) {
      if (region) region.textContent = message;
    };
  })();

  /* Shopify serves the cart routes under the active locale/market prefix. Taking
     them from Liquid (layout/theme.liquid) instead of writing '/cart/add.js' is
     the difference between add-to-cart working and 404ing on a store that has
     more than one market. The literals are only a last resort. */
  var ROUTES = window.themeRoutes || {};

  function cartApi(key, fallback) {
    return (ROUTES[key] || fallback) + '.js';
  }

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw data;
        return data;
      });
    });
  }

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
      var link = node.closest('[data-cart-link]');
      if (link) link.setAttribute('aria-label', (node.getAttribute('data-cart-label') || '').replace('COUNT', count));
    });
  }

  /* ------------------------------------------------------- header appearance */

  (function header() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    /* Turned off in the header settings, the header stays solid everywhere. */
    var darkZones = header.getAttribute('data-transparent') === 'false'
      ? []
      : document.querySelectorAll('[data-header-theme="dark"]');

    function onScroll() {
      header.setAttribute('data-condensed', window.scrollY > 24 ? 'true' : 'false');
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* Transitions are off in CSS until this lands. Next frame, not this one:
       enabling them in the same task as the first appearance change would
       animate that change, which is the flash this exists to prevent. Set
       before the early return below so a page with no dark sections still
       ends up with a header that animates on scroll. */
    window.requestAnimationFrame(function () {
      header.setAttribute('data-appearance-ready', 'true');
    });

    if (!darkZones.length) return;

    /* Resolve the initial state synchronously. The observer alone would leave
       the header in its light default until the first scroll event. */
    function evaluateNow() {
      var band = header.offsetHeight;
      var dark = false;
      darkZones.forEach(function (zone) {
        var rect = zone.getBoundingClientRect();
        if (rect.top < band && rect.bottom > 0) dark = true;
      });
      header.setAttribute('data-appearance', dark ? 'dark' : 'light');
    }

    evaluateNow();

    if (!('IntersectionObserver' in window)) {
      window.addEventListener('scroll', evaluateNow, { passive: true });
      return;
    }

    var live = new Set();

    function build() {
      var band = Math.max(0, window.innerHeight - header.offsetHeight);
      return new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) live.add(entry.target);
            else live.delete(entry.target);
          });
          header.setAttribute('data-appearance', live.size ? 'dark' : 'light');
        },
        { rootMargin: '0px 0px -' + band + 'px 0px' }
      );
    }

    var observer = build();
    darkZones.forEach(function (zone) {
      observer.observe(zone);
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        observer.disconnect();
        live.clear();
        observer = build();
        darkZones.forEach(function (zone) {
          observer.observe(zone);
        });
      }, 180);
    });
  })();

  /* ------------------------------------------------------- header dropdowns */

  /* The panels open and close on their own — they are native <details>, which
     is why a two-level menu still works before this file loads. All that is
     missing is dismissal, which <details> has no opinion about. */
  (function navDropdowns() {
    var drops = document.querySelectorAll('[data-nav-drop]');
    if (!drops.length) return;

    function closeAll(except) {
      drops.forEach(function (drop) {
        if (drop !== except) drop.open = false;
      });
    }

    drops.forEach(function (drop) {
      drop.addEventListener('toggle', function () {
        if (drop.open) closeAll(drop);
      });
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('[data-nav-drop]')) closeAll(null);
    });

    document.addEventListener('focusin', function (event) {
      if (!event.target.closest('[data-nav-drop]')) closeAll(null);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = document.querySelector('[data-nav-drop][open]');
      if (!open) return;
      var summary = open.querySelector('summary');
      open.open = false;
      if (summary) summary.focus();
    });
  })();

  /* ------------------------------------------------------------ mobile menu */

  (function mobileMenu() {
    var menu = document.querySelector('[data-mobile-menu]');
    var openers = document.querySelectorAll('[data-menu-open]');
    if (!menu || !openers.length) return;

    var closer = menu.querySelector('[data-menu-close]');
    var lastFocused = null;

    function focusables() {
      return menu.querySelectorAll('a[href], button:not([disabled])');
    }

    function open() {
      lastFocused = document.activeElement;
      menu.setAttribute('data-open', 'true');
      menu.removeAttribute('aria-hidden');
      document.body.setAttribute('data-scroll-locked', 'true');
      openers.forEach(function (o) {
        o.setAttribute('aria-expanded', 'true');
      });
      var first = focusables()[0];
      if (first) first.focus();
    }

    function close() {
      menu.setAttribute('data-open', 'false');
      menu.setAttribute('aria-hidden', 'true');
      document.body.removeAttribute('data-scroll-locked');
      openers.forEach(function (o) {
        o.setAttribute('aria-expanded', 'false');
      });
      if (lastFocused) lastFocused.focus();
    }

    openers.forEach(function (o) {
      o.addEventListener('click', open);
    });
    if (closer) closer.addEventListener('click', close);

    menu.addEventListener('click', function (event) {
      if (event.target.closest('a')) close();
    });

    document.addEventListener('keydown', function (event) {
      if (menu.getAttribute('data-open') !== 'true') return;

      if (event.key === 'Escape') {
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  })();

  /* ------------------------------------------------------- reveal on scroll */

  (function reveals() {
    var unobserved =
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var observer = unobserved
      ? null
      : new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              entry.target.setAttribute('data-revealed', 'true');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
        );

    /* Elements start at opacity 0 and are shown only once observed, so anything
       that arrives later has to be picked up — otherwise it stays invisible. */
    function bind() {
      document.querySelectorAll('[data-reveal]:not([data-revealed])').forEach(function (t) {
        if (observer) observer.observe(t);
        else t.setAttribute('data-revealed', 'true');
      });
    }

    bind();

    /* The theme editor replaces a section's markup wholesale on every edit.
       The replacement's nodes are new, so they are not in the observer and
       never reveal — which looks to the merchant like editing a field deleted
       the section's copy. Re-scan instead. bindQuantity is idempotent and has
       the same problem, so it rides along. */
    document.addEventListener('shopify:section:load', function () {
      bind();
      bindQuantity(document);
    });

    /* Markup the theme swaps in itself (live search results) needs the same. */
    document.addEventListener('theme:content-added', bind);
  })();

  /* ------------------------------------------------------- country selects */

  /* all_country_option_tags renders the options but cannot mark one selected,
     so the address form carries the saved country on the select itself and it
     is applied here. Without this, editing an address silently resets it to
     the first country in the list. */
  document.querySelectorAll('select[data-default-country]').forEach(function (select) {
    var value = select.getAttribute('data-default-country');
    if (value) select.value = value;
  });

  /* -------------------------------------------------- selects that navigate */

  document.querySelectorAll('[data-auto-submit]').forEach(function (control) {
    control.addEventListener('change', function () {
      if (control.form) control.form.submit();
    });
  });

  /* ---------------------------------------------------------- quantity step */

  function bindQuantity(scope) {
    scope.querySelectorAll('[data-qty]').forEach(function (widget) {
      if (widget.dataset.bound === 'true') return;
      widget.dataset.bound = 'true';

      var input = widget.querySelector('[data-qty-input]');
      var minus = widget.querySelector('[data-qty-minus]');
      var plus = widget.querySelector('[data-qty-plus]');
      if (!input) return;

      function sync() {
        var value = parseInt(input.value, 10);
        var min = parseInt(input.min, 10) || 0;
        var max = input.max ? parseInt(input.max, 10) : Infinity;
        if (isNaN(value)) value = min;
        value = Math.min(Math.max(value, min), max);
        input.value = value;
        if (minus) minus.disabled = value <= min;
        if (plus) plus.disabled = value >= max;
      }

      function step(delta) {
        input.value = (parseInt(input.value, 10) || 0) + delta;
        sync();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (minus) minus.addEventListener('click', function () { step(-1); });
      if (plus) plus.addEventListener('click', function () { step(1); });
      input.addEventListener('change', sync);
      sync();
    });
  }

  bindQuantity(document);

  /* ------------------------------------------------------ card hover video */

  /* A product with a video previews it on hover. The video was rendered with
     preload="none", so the first hover is what starts the download; it only
     becomes visible once frames are actually playing. Touch screens have no
     hover, so they keep the photo. */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.addEventListener('mouseover', function (event) {
      var card = event.target.closest && event.target.closest('.card');
      if (!card || card.contains(event.relatedTarget)) return;
      var video = card.querySelector('[data-card-video] video');
      if (!video) return;
      video.muted = true;
      video.preload = 'auto';
      var attempt = video.play();
      if (attempt) attempt.catch(function () {});
    });

    document.addEventListener('mouseout', function (event) {
      var card = event.target.closest && event.target.closest('.card');
      if (!card || card.contains(event.relatedTarget)) return;
      var video = card.querySelector('[data-card-video] video');
      if (!video) return;
      video.pause();
      video.parentNode.classList.remove('is-playing');
    });

    /* If the browser let the first play() lapse while the file was still
       arriving, start it once it can play, provided the cursor is still on
       the card. */
    document.addEventListener('canplay', function (event) {
      var video = event.target;
      if (!video.matches || !video.matches('[data-card-video] video')) return;
      var card = video.closest('.card');
      if (card && card.matches(':hover') && video.paused) video.play().catch(function () {});
    }, true);

    document.addEventListener('playing', function (event) {
      if (event.target.matches && event.target.matches('[data-card-video] video')) event.target.parentNode.classList.add('is-playing');
    }, true);
  }

  /* ---------------------------------------------------------------- gallery */

  /* Two ways to move, one state. With a fine pointer the slides are stacked
     and crossfade (theme.css), so moving is a class swap. On touch they sit in
     a scroll-snapped row and moving is a scroll; swiping scrolls natively and
     the index is read back from the scroll position. The fade check reads the
     same media query the CSS uses, so the two can never disagree. */
  function bindGallery(gallery) {
    var track = gallery.querySelector('[data-gallery-track]');
    var slides = gallery.querySelectorAll('[data-gallery-slide]');
    if (!track) return;
    if (slides.length < 2) {
      var lone = slides[0] && slides[0].querySelector('video');
      if (lone && gallery.getAttribute('data-autoplay-video') === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        lone.play().catch(function () {});
      } else if (lone) {
        lone.controls = true;
      }
      return;
    }

    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    var current = gallery.querySelector('[data-gallery-current]');
    var progress = gallery.querySelector('[data-gallery-progress]');
    var fade = window.matchMedia('(hover: hover) and (pointer: fine)');
    var index = parseInt(gallery.getAttribute('data-start'), 10) || 0;
    var stillness = window.matchMedia('(prefers-reduced-motion: reduce)');
    var autoplay = gallery.getAttribute('data-autoplay-video') === 'true' && !stillness.matches;

    /* Only the showing slide's video runs. A visitor who paused one keeps it
       paused when they come back to it. Reduced motion: never autoplay, and
       hand over the native controls instead. */
    function syncVideos() {
      slides.forEach(function (slide, i) {
        var video = slide.querySelector('video');
        if (!video) return;
        if (!autoplay) video.controls = true;
        var paused = slide.getAttribute('data-user-paused') === 'true';
        if (i === index && autoplay && !paused) {
          var attempt = video.play();
          if (attempt) attempt.catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    gallery.querySelectorAll('[data-gallery-video-toggle]').forEach(function (button) {
      var slide = button.closest('[data-gallery-slide]');
      var video = slide.querySelector('video');
      button.addEventListener('click', function () {
        /* Follow the visitor's last choice, not video.paused: while a video
           is still buffering it reads as paused, and the first press would
           then start it instead of stopping it. */
        var pause = slide.getAttribute('data-user-paused') !== 'true';
        slide.setAttribute('data-user-paused', pause ? 'true' : 'false');
        button.setAttribute('aria-pressed', pause ? 'true' : 'false');
        button.setAttribute('aria-label', button.getAttribute(pause ? 'data-label-play' : 'data-label-pause'));
        if (pause) video.pause();
        else video.play().catch(function () {});
      });
    });

    function show(next, fromScroll) {
      index = (next + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      thumbs.forEach(function (thumb, i) {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (current) current.textContent = String(index + 1).padStart(2, '0');
      if (progress) progress.style.setProperty('--index', index);
      syncVideos();
      if (!fade.matches && !fromScroll) {
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }
    }

    var prev = gallery.querySelector('[data-gallery-prev]');
    var next = gallery.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        show(parseInt(thumb.getAttribute('data-gallery-thumb'), 10));
      });
    });

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
    });

    var settle;
    track.addEventListener('scroll', function () {
      if (fade.matches) return;
      window.clearTimeout(settle);
      settle = window.setTimeout(function () {
        var i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== index) show(i, true);
      }, 80);
    }, { passive: true });

    /* Switching modes (a resized window, a tablet gaining a mouse) leaves the
       track wherever the other mode put it, so re-seat it on the active slide. */
    fade.addEventListener('change', function () {
      track.scrollLeft = fade.matches ? 0 : slides[index].offsetLeft - track.offsetLeft;
    });

    if (index && !fade.matches) track.scrollLeft = slides[index].offsetLeft - track.offsetLeft;
    syncVideos();

    gallery.goToMedia = function (mediaId) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) return show(i);
      }
    };
  }

  document.querySelectorAll('[data-gallery]').forEach(bindGallery);

  /* ----------------------------------------------------------- live search */

  /* Results refresh as the customer types. The request asks Shopify for this
     section alone (Section Rendering API), so the server still does the
     searching and the markup stays the theme's own; only the results block is
     swapped. A stale response is dropped if a newer keystroke overtook it.
     Without the script the form still submits normally. */
  (function liveSearch() {
    var root = document.querySelector('[data-search-root]');
    if (!root) return;

    var form = root.querySelector('[data-search-form]');
    var input = root.querySelector('[data-search-input]');
    var results = root.querySelector('[data-search-results]');
    var sectionId = root.getAttribute('data-section-id');
    var timer;
    var latest = 0;

    function run() {
      var params = new URLSearchParams(new FormData(form));
      var ticket = ++latest;
      results.setAttribute('aria-busy', 'true');

      var url = form.action + '?' + params.toString();
      fetch(url + '&section_id=' + encodeURIComponent(sectionId))
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          if (ticket !== latest) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-search-results]');
          if (fresh) results.innerHTML = fresh.innerHTML;
          results.removeAttribute('aria-busy');
          window.history.replaceState({}, '', input.value.trim() ? url : form.action);
          document.dispatchEvent(new CustomEvent('theme:content-added'));
        })
        .catch(function () {
          if (ticket === latest) form.submit();
        });
    }

    input.addEventListener('input', function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, 280);
    });
  })();

  /* ------------------------------------------------------------ product form */

  (function productForm() {
    var root = document.querySelector('[data-product-root]');
    if (!root) return;

    var data = root.querySelector('[data-product-variants]');
    var variants = data ? JSON.parse(data.textContent) : [];
    var form = root.querySelector('[data-product-form]');
    var idInput = root.querySelector('[data-variant-id]');
    var submit = root.querySelector('[data-add-to-cart]');
    var submitText = submit ? submit.querySelector('[data-add-to-cart-text]') : null;
    var priceSlot = root.querySelector('[data-variant-price]');
    var availability = root.querySelector('[data-variant-availability]');
    var skuSlot = root.querySelector('[data-variant-sku]');
    var feedback = root.querySelector('[data-form-feedback]');
    var qtyInput = root.querySelector('[data-qty-input]');
    var optionInputs = root.querySelectorAll('[data-option-index]');
    var stringsNode = root.querySelector('[data-product-strings]');
    var strings = stringsNode ? JSON.parse(stringsNode.textContent) : {};

    function selected() {
      var values = [];
      optionInputs.forEach(function (input) {
        if (input.checked) values[parseInt(input.getAttribute('data-option-index'), 10)] = input.value;
      });
      return values;
    }

    function findVariant(values) {
      return variants.find(function (variant) {
        return variant.options.every(function (option, index) {
          return option === values[index];
        });
      });
    }

    function updateOptionLabels(values) {
      root.querySelectorAll('[data-option-selected]').forEach(function (node) {
        var index = parseInt(node.getAttribute('data-option-selected'), 10);
        node.textContent = values[index] || '';
      });
    }

    function render(variant) {
      if (priceSlot && variant) priceSlot.innerHTML = variant.price_html;
      if (skuSlot) skuSlot.textContent = variant && variant.sku ? variant.sku : '';

      var available = !!(variant && variant.available);

      if (availability) {
        availability.setAttribute('data-available', available ? 'true' : 'false');
        availability.textContent = variant
          ? available
            ? strings.in_stock
            : strings.sold_out
          : strings.unavailable;
      }

      if (idInput && variant) idInput.value = variant.id;

      /* Each variant carries its own stock ceiling, so switching variants has
         to move the ceiling with it — otherwise a customer who picked 6 of a
         well-stocked size keeps 6 after switching to one with 2 left, and only
         finds out when the add fails. 0 means Shopify imposes no limit here
         (untracked, or set to keep selling when out of stock). The change event
         is what makes the stepper re-clamp the value it is already holding. */
      if (qtyInput) {
        var cap = variant && variant.qty_max > 0 ? variant.qty_max : 0;
        if (cap) qtyInput.max = cap;
        else qtyInput.removeAttribute('max');
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (submit) {
        submit.disabled = !available;
        if (submitText) {
          submitText.textContent = !variant
            ? strings.unavailable
            : available
            ? strings.add_to_cart
            : strings.sold_out;
        }
      }

      /* Show the image tied to the variant, if the merchant set one. The
         carousel moves inside its own frame, so this never scrolls the page
         out from under the swatch the customer just tapped. */
      var gallery = root.querySelector('[data-gallery]');
      if (variant && variant.media_id && gallery && gallery.goToMedia) gallery.goToMedia(variant.media_id);

      if (variant && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var values = selected();
        updateOptionLabels(values);
        render(findVariant(values));
      });
    });

    if (!form) return;

    var feedbackTimer;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!submit || submit.disabled) return;

      submit.setAttribute('data-loading', 'true');
      if (feedback) feedback.setAttribute('data-visible', 'false');

      fetch(cartApi('cartAdd', '/cart/add'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function () {
          return fetch(cartApi('cart', '/cart'), { headers: { Accept: 'application/json' } }).then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          setCartCount(cart.item_count);
          announce(strings.added);
          if (feedback) {
            feedback.setAttribute('data-state', 'success');
            feedback.innerHTML =
              '<span>' + strings.added + '</span><a href="' + (strings.cart_url || '/cart') + '">' + strings.view_cart + '</a>';
            feedback.setAttribute('data-visible', 'true');
            clearTimeout(feedbackTimer);
            feedbackTimer = setTimeout(function () {
              feedback.setAttribute('data-visible', 'false');
            }, 6000);
          }
        })
        .catch(function (error) {
          if (feedback) {
            feedback.setAttribute('data-state', 'error');
            feedback.textContent = (error && error.description) || strings.error;
            feedback.setAttribute('data-visible', 'true');
          }
          announce((error && error.description) || strings.error);
        })
        .finally(function () {
          submit.removeAttribute('data-loading');
        });
    });

    /* Initialise from whichever variant Liquid rendered as selected */
    var initial = selected();
    if (initial.length) updateOptionLabels(initial);
  })();

  /* -------------------------------------------------------- recommendations */

  (function recommendations() {
    var host = document.querySelector('[data-recommendations]');
    if (!host || !host.getAttribute('data-url')) return;

    fetch(host.getAttribute('data-url'))
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-recommendations]');
        if (!fresh || !fresh.innerHTML.trim()) return;
        host.innerHTML = fresh.innerHTML;
        host.querySelectorAll('[data-reveal]').forEach(function (node) {
          node.setAttribute('data-revealed', 'true');
        });
      })
      .catch(function () {
        /* Recommendations are a nicety; a failure leaves the page intact. */
      });
  })();

  /* ------------------------------------------------------------------- cart */

  (function cart() {
    var root = document.querySelector('[data-cart-root]');
    if (!root) return;

    /* Marks the AJAX cart as live, which hides the native update button. Set
       here rather than in CSS on `.js` so that a theme.js that fails to load
       leaves the fallback on screen — the case <noscript> cannot cover. The
       attribute sits on the root, which refresh() does not replace. */
    root.setAttribute('data-js-cart', 'true');

    /* Re-render the cart SECTION, not the whole page. Shopify's Section
       Rendering API serves just this section's markup, so an increment costs
       one small response instead of a full page render — header, footer,
       story CSS and all — parsed and thrown away. */
    function refresh() {
      var sectionId = root.getAttribute('data-cart-section');
      /* Without the id there is nothing to ask the Section Rendering API for,
         so fall back to re-reading the page — slower, but never wrong. */
      var url = sectionId
        ? (ROUTES.cart || '/cart') + '?section_id=' + encodeURIComponent(sectionId)
        : window.location.href;
      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-root]');
          if (!fresh) {
            window.location.reload();
            return;
          }
          /* The swap destroys the control the customer is using — usually the
             + button, since that is what they just clicked. Remember the line
             and the control, and give both back; otherwise every increment
             drops a keyboard user back to the top of the document. */
          var active = document.activeElement;
          var host = active && active.closest ? active.closest('[data-cart-line]') : null;
          var focusKey = null;
          var focusSelector = '[data-cart-qty]';
          if (host && root.contains(host)) {
            var keyed = host.querySelector('[data-cart-qty]');
            focusKey = keyed && keyed.getAttribute('data-key');
            if (active.matches('[data-qty-plus]')) focusSelector = '[data-qty-plus]';
            else if (active.matches('[data-qty-minus]')) focusSelector = '[data-qty-minus]';
          }

          root.innerHTML = fresh.innerHTML;
          bindQuantity(root);

          if (focusKey) {
            var line = root.querySelector('[data-cart-qty][data-key="' + focusKey + '"]');
            var restoredHost = line && line.closest('[data-cart-line]');
            var target = restoredHost && restoredHost.querySelector(focusSelector);
            /* The stepper disables itself at the ends of the range, so fall
               back to the input rather than focusing a dead button. */
            if (target && !target.disabled) target.focus();
            else if (line) line.focus();
          }
        });
    }

    /* One request at a time, in order. Three quick clicks on + used to fire
       three change calls and three page fetches whose responses could land out
       of order, leaving the cart on screen out of step with the real one. */
    var chain = Promise.resolve();

    function change(key, quantity, row) {
      if (row) row.setAttribute('data-busy', 'true');
      chain = chain.then(function () {
        return request(cartApi('cartChange', '/cart/change'), { id: key, quantity: quantity })
          .then(function (data) {
            setCartCount(data.item_count);
            return refresh();
          })
          .catch(function () {
            window.location.reload();
          });
      });
      return chain;
    }

    /* Holding down + walks 1 -> 4 in three events. Send the last one only. */
    var timers = {};

    function debouncedChange(key, read, row) {
      clearTimeout(timers[key]);
      timers[key] = setTimeout(function () {
        delete timers[key];
        change(key, read(), row);
      }, 300);
    }

    root.addEventListener('change', function (event) {
      var input = event.target.closest('[data-cart-qty]');
      if (!input) return;
      var row = input.closest('[data-cart-line]');
      debouncedChange(
        input.getAttribute('data-key'),
        function () {
          return parseInt(input.value, 10) || 0;
        },
        row
      );
    });

    root.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      var key = remove.getAttribute('data-key');
      var row = remove.closest('[data-cart-line]');
      /* A removal must not sit behind a pending increment for the same line. */
      clearTimeout(timers[key]);
      delete timers[key];
      announce(remove.getAttribute('data-removed-label') || '');
      change(key, 0, row);
    });

    /* Discount codes are applied by Shopify's own discount route.
       Delegated, because refresh() replaces the markup underneath us. */
    function submitDiscount() {
      var input = root.querySelector('[data-discount-input]');
      var code = input ? (input.value || '').trim() : '';
      if (!code) return;
      window.location.href =
        (ROUTES.root === '/' ? '' : ROUTES.root || '') + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
    }

    root.addEventListener('click', function (event) {
      if (!event.target.closest('[data-discount-apply]')) return;
      event.preventDefault();
      submitDiscount();
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' || !event.target.closest('[data-discount-input]')) return;
      event.preventDefault();
      submitDiscount();
    });

    /* Order note saves on blur, not on every keystroke */
    root.addEventListener(
      'blur',
      function (event) {
        var note = event.target.closest('[data-cart-note]');
        if (!note) return;
        request(cartApi('cartUpdate', '/cart/update'), { note: note.value });
      },
      true
    );
  })();
})();
